/**
 * @author: lv.chen
 * @date: 2026/3/3
 * @desc: LowCodeTemplateWrapper组件
 */
import React from 'react';
import classnames from 'classnames';
import { Alert } from 'tea-component';
import Loading from '@intelligence/components/Loading';
import LowCodeTemplate from '@intelligence/pages/LowCodeTemplate';
import LowCodeTemplateWithDataJSON from '@intelligence/pages/LowCodeTemplateDataJson';
import LowCodeTemplateMultiLocaleDataJson from '@intelligence/pages/LowCodeTemplateMultiLocaleDataJson';
import usePageLog from '@intelligence/v3/_hooks/usePageLog';
import useLowCodeTemplateConfig from './hooks/useLowCodeTemplateConfig';
import { fetchDBIGZTencentQCouldMassage, fetchDBISingaporeTencentQCouldMassage } from '@intelligence/services/cos';
import { getValidCOSRegion } from './utils';

import type { ITencentQCouldMessageFetcher, IFetchTencentQCouldMessage } from '@intelligence/services/cos';

interface ILowCodeTemplateCommonProps extends IFetchTencentQCouldMessage {
  config: IRecord;
}

interface ILowCodeTemplateWrapperProps extends IBaseProps {}

const LowCodeTemplateComponentMap: IRecord<React.FC<ILowCodeTemplateCommonProps>> = {
  1: LowCodeTemplate,
  2: LowCodeTemplateWithDataJSON,
  3: LowCodeTemplateMultiLocaleDataJson,
};

const fetchTencentQCouldMessageMap: IRecord<ITencentQCouldMessageFetcher> = {
  guangzhou: fetchDBIGZTencentQCouldMassage,
  singapore: fetchDBISingaporeTencentQCouldMassage,
};

const LowCodeTemplateWrapper: React.FC<ILowCodeTemplateWrapperProps> = (props) => {
  const { style, className } = props;

  const searchParams = new URLSearchParams(location.search);
  const templateType = searchParams.get('lowcode_template_type') || '';
  const templateKey = searchParams.get('lowcode_template_key') || '';
  const pageLogKey = searchParams.get('page_log_key') || '';

  // 页面打点
  usePageLog(pageLogKey);

  // 获取模板配置
  const { loading, error, config } = useLowCodeTemplateConfig(templateKey);

  // 获取模板存储的cos的region
  const cosRegion = getValidCOSRegion(config?.cos_region);

  // 选择 COS fetcher
  const fetchTencentQCouldMessage = fetchTencentQCouldMessageMap[cosRegion] || fetchDBIGZTencentQCouldMassage;

  // 处理配置加载状态
  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 180px)',
          color: '#262832',
        }}
      >
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="dbi-cos-page-error">
        <Alert>Failed to load template config: {error instanceof Error ? error?.message : 'Unknown error'}</Alert>
      </div>
    );
  }

  // 关键：只有获取到 config 后才渲染子组件
  if (!config) {
    return (
      <div className="dbi-cos-page-error">
        <Alert>LowCodeTemplate config of `{templateKey}` not found.</Alert>
      </div>
    );
  }

  const LowCodeTemplateComponent = LowCodeTemplateComponentMap[templateType];

  if (!LowCodeTemplateComponent) {
    return (
      <div className="dbi-cos-page-error">
        <Alert>LowCodeTemplate of type `{templateType}` not found.</Alert>
      </div>
    );
  }

  return (
    <div style={style} className={classnames('dbi-lowcode-template-wrapper', className)}>
      {/* 传递 fetcher 给子组件（子组件继续负责 COS 初始化） */}
      <LowCodeTemplateComponent config={config} fetchTencentQCouldMessage={fetchTencentQCouldMessage} />
    </div>
  );
};

export default LowCodeTemplateWrapper;
